
import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";

import RenderSanityStep from "../../components/lesson/renderSanityStep";

import {
  getCurrentSegmentFromWildcard,
  getStepSegment,
  normalizeLevelKey,
  normalizeQuestKey,
} from "../../lib/sanity/sanityLevels";

import {
  getLevelResumeProgress,
  saveLevelResumeProgress,
} from "../../lib/learning/levelResumeProgress";

function isConceptStartStep(step) {
  return step?._type === "keyConceptStep";
}

function isResultStep(step) {
  return step?._type === "resultStep";
}

function isLegacyFinalQuizStep(step) {
  return (
    step?._type === "quizSelectStep" ||
    step?._type === "quizDragStep"
  );
}

function isProgressStep(step) {
  if (!step) return false;
  return !isResultStep(step);
}

function getConceptTitleFromStep(step) {
  return (
    step?.conceptTitle ||
    step?.concept?.title ||
    step?.displayTitle ||
    step?.heading ||
    step?.title ||
    ""
  );
}

function getCurrentConceptTitle(steps, currentIndex) {
  if (!steps?.length || currentIndex < 0) return "";

  for (let i = currentIndex; i >= 0; i -= 1) {
    if (isConceptStartStep(steps[i])) {
      return getConceptTitleFromStep(steps[i]);
    }
  }

  return "";
}

function getCurrentConceptNumber(steps, currentIndex) {
  if (!steps?.length || currentIndex < 0) return 1;

  let count = 0;

  for (let i = 0; i <= currentIndex; i += 1) {
    if (isConceptStartStep(steps[i])) {
      count += 1;
    }
  }

  return Math.max(count, 1);
}

function getConceptTotal(steps) {
  return Math.max(
    steps.filter(isConceptStartStep).length,
    1
  );
}

function getLevelTitle(level) {
  return (
    level?.levelName ||
    level?.levelTitle ||
    level?.displayTitle ||
    level?.title ||
    level?.heading ||
    level?.name ||
    ""
  );
}

function getStepPath(basePath, step) {
  if (!basePath || !step) return null;

  if (isResultStep(step)) {
    return `${basePath}/result`;
  }

  if (!step.__segment) return null;

  return `${basePath}/${step.__segment}`;
}

function isEntrySegment(segment) {
  return (
    !segment ||
    segment === "welcome" ||
    segment === "overview"
  );
}

function findResumeStep(steps, progress) {
  if (!progress || !steps?.length) return null;

  const savedKey = progress.resume_step_key;
  const savedSegment = progress.resume_segment;
  const savedIndex = Number(progress.resume_step_index);
  const savedType = progress.resume_step_type;

  // Most reliable: permanent Sanity step key.
  if (savedKey) {
    const match = steps.find(
      (step) => String(step._key) === String(savedKey)
    );

    if (match && !isResultStep(match)) {
      return match;
    }
  }

  // Fallback: previously generated route segment.
  if (savedSegment) {
    const match = steps.find(
      (step) => step.__segment === savedSegment
    );

    if (match && !isResultStep(match)) {
      return match;
    }
  }

  // Final fallback: index, but only when the step type
  // still matches. Avoid sending learners to an unrelated
  // page if the content manager reordered the lesson.
  if (
    Number.isInteger(savedIndex) &&
    savedIndex >= 0 &&
    savedIndex < steps.length
  ) {
    const match = steps[savedIndex];

    if (
      match &&
      !isResultStep(match) &&
      (!savedType || match._type === savedType)
    ) {
      return match;
    }
  }

  return null;
}

export default function SanityFlowRunner({ level }) {
  const navigate = useNavigate();
  const params = useParams();

  const { questId, subquestId, levelKey } = params;
  const wildcard = params["*"];

  const normalizedQuestId = normalizeQuestKey(questId);
  const normalizedLevelKey = normalizeLevelKey(levelKey);

  const currentSegment =
    getCurrentSegmentFromWildcard(wildcard);

  const basePath =
    `/play/${normalizedQuestId}/${subquestId}/${normalizedLevelKey}`;

  const routeIdentity = [
    normalizedQuestId,
    subquestId,
    normalizedLevelKey,
  ].join("|");

  const stepsWithSegments = useMemo(() => {
    const steps = level?.steps || [];

    return steps
      .filter((step) => !isLegacyFinalQuizStep(step))
      .map((step, index) => ({
        ...step,
        __segment: getStepSegment(step),
        __index: index,
      }));
  }, [level]);

  // Sanity is the source of truth for whether this level has a Practical Action.
  // A standalone introduction without questions is incomplete: do not grant A-XP.
  const practicalActionInfo = useMemo(() => {
    const actionSteps = stepsWithSegments.filter(
      (step) => step._type === "practicalActionDraftStep"
    );
    const questionSteps = stepsWithSegments.filter(
      (step) => step._type === "actionQuestionDraftStep"
    );
    return {
      hasPracticalAction: actionSteps.length > 0 && questionSteps.length > 0,
      actionQuestionCount: questionSteps.length,
    };
  }, [stepsWithSegments]);

  const currentIndex = useMemo(() => {
    return stepsWithSegments.findIndex(
      (step) => step.__segment === currentSegment
    );
  }, [stepsWithSegments, currentSegment]);

  const currentStep =
    currentIndex >= 0
      ? stepsWithSegments[currentIndex]
      : stepsWithSegments[0];

  const levelTitle = useMemo(
    () => getLevelTitle(level),
    [level]
  );

  /*
   * Resume lookup state is tied to the current level.
   * This prevents a saved position from one level being
   * applied to another when React reuses the component.
   */
  const [resumeState, setResumeState] = useState({
    routeIdentity: null,
    status: "pending",
    resumeSegment: null,
  });

  const resumeReady =
    resumeState.routeIdentity === routeIdentity &&
    resumeState.status === "ready";

  /*
   * Fetch saved progress BEFORE saving a new position.
   *
   * Do not write the Overview page over an existing
   * resume record while this lookup is running.
   */
  useEffect(() => {
    if (!level || !stepsWithSegments.length) {
      return;
    }

    let cancelled = false;

    setResumeState({
      routeIdentity,
      status: "pending",
      resumeSegment: null,
    });

    async function restoreResume() {
      try {
        const saved = await getLevelResumeProgress({
          questId: normalizedQuestId,
          subquestId,
          levelKey: normalizedLevelKey,
        });

        if (cancelled) return;

        const matchedStep = findResumeStep(
          stepsWithSegments,
          saved
        );

        console.log("[Gwin Resume] Restore result:", {
          levelKey: normalizedLevelKey,
          savedSegment: saved?.resume_segment || null,
          savedStepKey: saved?.resume_step_key || null,
          matchedSegment: matchedStep?.__segment || null,
        });

        setResumeState({
          routeIdentity,
          status: "ready",
          resumeSegment: matchedStep?.__segment || null,
        });
      } catch (error) {
        if (cancelled) return;

        console.warn(
          "[Gwin Resume] Could not restore progress:",
          error
        );

        /*
         * Allow an explicitly requested lesson page
         * to work even if the resume lookup fails.
         * Do not save over an unknown previous position.
         */
        setResumeState({
          routeIdentity,
          status: "error",
          resumeSegment: null,
        });
      }
    }

    restoreResume();

    return () => {
      cancelled = true;
    };
  }, [
    level,
    stepsWithSegments,
    routeIdentity,
    normalizedQuestId,
    subquestId,
    normalizedLevelKey,
  ]);

  /*
   * Restore only when entering through Overview/Welcome
   * or through the bare level URL.
   *
   * Explicit links to lesson pages are respected.
   */
  const shouldResume =
    resumeReady &&
    isEntrySegment(currentSegment) &&
    !!resumeState.resumeSegment &&
    resumeState.resumeSegment !== currentSegment;

  useEffect(() => {
    if (!shouldResume) return;

    const target =
      `${basePath}/${resumeState.resumeSegment}`;

    console.log("[Gwin Resume] Navigating to:", target);

    navigate(target, { replace: true });
  }, [
    shouldResume,
    basePath,
    resumeState.resumeSegment,
    navigate,
  ]);

  /*
   * Save the current position after the initial
   * resume lookup has completed.
   *
   * Do not save Overview, Result, or an intermediate
   * Overview page while redirecting to a saved step.
   */
  useEffect(() => {
    if (
      !level ||
      !resumeReady ||
      shouldResume ||
      currentIndex < 0 ||
      !currentStep
    ) {
      return;
    }

    if (
      currentStep._type === "welcomeStep" ||
      isResultStep(currentStep) ||
      !currentStep.__segment
    ) {
      return;
    }

    let active = true;

    saveLevelResumeProgress({
      questId: normalizedQuestId,
      subquestId,
      levelKey: normalizedLevelKey,
      levelTitle,

      resumeSegment: currentStep.__segment,
      resumeStepKey: currentStep._key || null,
      resumeStepIndex: currentStep.__index,
      resumeStepType: currentStep._type || null,
    }).catch((error) => {
      if (active) {
        console.warn(
          "[Gwin Resume] Could not save progress:",
          error
        );
      }
    });

    return () => {
      active = false;
    };
  }, [
    level,
    resumeReady,
    shouldResume,
    currentIndex,
    currentStep,
    normalizedQuestId,
    subquestId,
    normalizedLevelKey,
    levelTitle,
  ]);

  /*
   * Keep the existing dashboard selection context.
   */
  useEffect(() => {
    localStorage.setItem(
      "selectedQuestId",
      String(normalizedQuestId || "")
    );

    localStorage.setItem(
      "selectedSubquestId",
      String(subquestId || "")
    );

    localStorage.setItem(
      "selectedLevelKey",
      String(normalizedLevelKey || "")
    );
  }, [
    normalizedQuestId,
    subquestId,
    normalizedLevelKey,
  ]);

  const progressSteps = useMemo(() => {
    return stepsWithSegments.filter(isProgressStep);
  }, [stepsWithSegments]);

  const progressTotal = Math.max(
    1,
    progressSteps.length
  );

  const progressStep = useMemo(() => {
    if (!currentStep) return 1;

    const index = progressSteps.findIndex(
      (step) =>
        step.__segment === currentStep.__segment ||
        step.__index === currentStep.__index
    );

    if (index >= 0) {
      return index + 1;
    }

    const previous = progressSteps
      .map((step, index) => ({ step, index }))
      .filter(
        ({ step }) =>
          step.__index < currentStep.__index
      )
      .map(({ index }) => index)
      .pop();

    return Number.isFinite(previous)
      ? previous + 1
      : 1;
  }, [currentStep, progressSteps]);

  const prevStep =
    currentIndex > 0
      ? stepsWithSegments[currentIndex - 1]
      : null;

  const nextStep =
    currentIndex >= 0 &&
    currentIndex < stepsWithSegments.length - 1
      ? stepsWithSegments[currentIndex + 1]
      : null;

  const prevTo = getStepPath(basePath, prevStep);
  const nextTo = getStepPath(basePath, nextStep);

  const conceptTitle = useMemo(
    () =>
      getCurrentConceptTitle(
        stepsWithSegments,
        currentIndex
      ),
    [stepsWithSegments, currentIndex]
  );

  const conceptNumber = useMemo(
    () =>
      getCurrentConceptNumber(
        stepsWithSegments,
        currentIndex
      ),
    [stepsWithSegments, currentIndex]
  );

  const conceptTotal = useMemo(
    () => getConceptTotal(stepsWithSegments),
    [stepsWithSegments]
  );

  if (!level) {
    return (
      <div className="p-4">
        No Sanity level loaded.
      </div>
    );
  }

  if (!stepsWithSegments.length) {
    return (
      <div className="p-4">
        This level has no steps yet.
      </div>
    );
  }

  /*
   * Wait until saved progress has been checked before
   * opening the lesson. This avoids a flash of Overview
   * and accidental progress overwrites.
   */
  if (!resumeReady) {
    if (
      resumeState.routeIdentity === routeIdentity &&
      resumeState.status === "error"
    ) {
      return (
        <div className="p-4" role="alert">
          Could not restore your saved position.
          Please refresh and try again.
        </div>
      );
    }

    return (
      <div className="p-4">
        Loading your lesson progress...
      </div>
    );
  }

  if (shouldResume) {
    return (
      <div className="p-4">
        Resuming your lesson...
      </div>
    );
  }

  if (currentIndex === -1) {
    return (
      <Navigate
        replace
        to={`${basePath}/${stepsWithSegments[0].__segment}`}
      />
    );
  }

  return (
    <RenderSanityStep
      step={currentStep}
      hasPracticalAction={practicalActionInfo.hasPracticalAction}
      actionQuestionCount={practicalActionInfo.actionQuestionCount}
      prevTo={prevTo}
      nextTo={nextTo}
      currentSegment={currentSegment}
      conceptTitle={conceptTitle}
      conceptNumber={conceptNumber}
      conceptTotal={conceptTotal}
      levelTitle={levelTitle}
      progressStep={progressStep}
      progressTotal={progressTotal}
    />
  );
}

